import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import Loading from '@/components/ui/Loading';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/stores/toast';
import {
  answerPublicTeleattendance,
  cancelPublicTeleattendanceQueueEntry,
  fetchPublicTeleattendanceSession,
  fetchPublicTeleattendanceVideoConnection,
  joinPublicTeleattendanceSession,
  startPublicTeleattendanceTriage,
} from '../services/teleattendanceService';
import { Clock3, Hospital, UserRound, Users, Video } from 'lucide-react';
import TeleattendanceVideoConference from '../components/TeleattendanceVideoConference';

type IntakeStep = 'details' | 'name' | 'email' | 'triage' | 'terms';

const formatElapsedTime = (startDate?: string | null) => {
  if (!startDate) return 'Agora';

  const start = dayjs(startDate);
  const diffMinutes = Math.max(dayjs().diff(start, 'minute'), 0);
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours === 0) return `${minutes} min`;
  return `${hours}h ${String(minutes).padStart(2, '0')}min`;
};

const sessionStatusTone: Record<string, string> = {
  link_generated: 'border-sky-200 bg-sky-50 text-sky-700',
  waiting_patient: 'border-amber-200 bg-amber-50 text-amber-700',
  in_progress: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  completed: 'border-slate-200 bg-slate-100 text-slate-700',
};

const queueStatusTone: Record<string, string> = {
  pending: 'border-sky-200 bg-sky-50 text-sky-700',
  waiting: 'border-amber-200 bg-amber-50 text-amber-700',
  in_progress: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  completed: 'border-slate-200 bg-slate-100 text-slate-700',
  cancelled: 'border-rose-200 bg-rose-50 text-rose-700',
};

const TRIAGE_MAX_QUESTIONS = 4;

const translatePublicTeleattendanceError = (
  message: string | undefined,
  t: (key: string) => string,
) => {
  if (!message) return '';

  const normalized = message.trim().toLowerCase();

  if (normalized === 'teleattendance link has expired') {
    return (
      t('teleattendance.link_expired_message') ||
      'Este link de teleatendimento expirou.'
    );
  }

  if (normalized === 'teleattendance link is not available yet') {
    return (
      t('teleattendance.link_not_available_yet_message') ||
      'Este link de teleatendimento ainda não está disponível.'
    );
  }

  if (normalized === 'teleattendance link not found') {
    return (
      t('teleattendance.public_not_found') ||
      'Não foi possível localizar essa sala pública de teleatendimento.'
    );
  }

  if (normalized === 'triage is required for this room') {
    return (
      t('teleattendance.triage_required_message') ||
      'Esta sala exige triagem antes da entrada na fila.'
    );
  }

  if (normalized === 'terms must be accepted before starting triage') {
    return (
      t('teleattendance.triage_terms_required_message') ||
      'Você precisa aceitar os termos antes de iniciar a triagem.'
    );
  }

  if (normalized === 'triage has not started yet') {
    return (
      t('teleattendance.triage_not_started_message') ||
      'A triagem ainda não foi iniciada neste link.'
    );
  }

  if (normalized === 'patient_age is invalid') {
    return (
      t('teleattendance.patient_age_invalid_message') ||
      'Informe uma idade válida para continuar.'
    );
  }

  if (normalized === 'patient_gender is required') {
    return (
      t('teleattendance.patient_gender_required_message') ||
      'Selecione uma opção de sexo/gênero para continuar.'
    );
  }

  return message;
};

const TeleattendancePublicPage = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [patientName, setPatientName] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [patientAge, setPatientAge] = useState('');
  const [patientGender, setPatientGender] = useState('');
  const [currentStep, setCurrentStep] = useState<IntakeStep>('details');
  const [triageAnswer, setTriageAnswer] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [triageSubmissionSnapshot, setTriageSubmissionSnapshot] = useState<{
    answer: string;
    isFinalQuestion: boolean;
  } | null>(null);
  const statusLabel: Record<string, string> = {
    link_generated:
      t('teleattendance.public_session_link_generated') ||
      'Aguardando entrada de pacientes',
    waiting_patient:
      t('teleattendance.public_session_waiting') || 'Fila em andamento',
    in_progress:
      t('teleattendance.public_session_in_progress') ||
      'Há uma consulta em andamento',
    completed:
      t('teleattendance.public_session_completed') ||
      'Último atendimento encerrado',
  };
  const queueStatusLabel: Record<string, string> = {
    pending:
      t('teleattendance.public_queue_pending') ||
      'Complete sua identificação para confirmar o atendimento',
    waiting:
      t('teleattendance.public_queue_waiting') ||
      'Você está aguardando o profissional',
    in_progress:
      t('teleattendance.public_queue_in_progress') ||
      'Seu atendimento começou',
    completed:
      t('teleattendance.public_queue_completed') ||
      'Seu atendimento foi encerrado',
    cancelled:
      t('teleattendance.public_queue_cancelled') ||
      'Sua entrada foi cancelada',
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyMinHeight = document.body.style.minHeight;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousHtmlMinHeight = document.documentElement.style.minHeight;

    document.body.style.overflow = 'auto';
    document.body.style.minHeight = '100%';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.minHeight = '100%';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.minHeight = previousBodyMinHeight;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.documentElement.style.minHeight = previousHtmlMinHeight;
    };
  }, []);

  const {
    data: session,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['teleattendance-public-session', token],
    queryFn: () => fetchPublicTeleattendanceSession(String(token || '')),
    enabled: Boolean(token),
    refetchInterval: 10000,
  });

  const joinMutation = useMutation({
    mutationFn: () =>
      joinPublicTeleattendanceSession(
        String(token || ''),
        patientName.trim(),
        patientEmail.trim(),
        Number(patientAge),
        patientGender,
      ),
    onSuccess: async (result) => {
      toast.success({
        title:
          t('teleattendance.toast_public_join_success_title') ||
          'Entrada confirmada',
        description:
          t('teleattendance.toast_public_join_success_description') ||
          'Seu atendimento foi identificado com sucesso neste link.',
      });
      await refetch();
    },
    onError: (error: any) => {
      toast.error({
        title:
          t('teleattendance.toast_public_join_error_title') ||
          'Não foi possível entrar na sala',
        description:
          translatePublicTeleattendanceError(
            error?.response?.data?.error,
            t as any,
          ) ||
          t('teleattendance.toast_public_join_error_description') ||
          'Revise seus dados e tente novamente.',
      });
    },
  });

  const startTriageMutation = useMutation({
    mutationFn: () =>
      startPublicTeleattendanceTriage(
        String(token || ''),
        patientName.trim(),
        patientEmail.trim(),
        Number(patientAge),
        patientGender,
        acceptedTerms,
      ),
    onSuccess: async (result) => {
      if (result.queueEntry?.queueStatus === 'waiting') {
        toast.success({
          title:
            t('teleattendance.toast_triage_completed_title') ||
            'Triagem concluída',
          description:
            t('teleattendance.toast_triage_completed_description') ||
            'Sua triagem foi concluída e você entrou na fila.',
        });
      }
      setCurrentStep('triage');
      await refetch();
    },
    onError: (error: any) => {
      toast.error({
        title:
          t('teleattendance.toast_triage_start_error_title') ||
          'Não foi possível iniciar a triagem',
        description:
          translatePublicTeleattendanceError(
            error?.response?.data?.error,
            t as any,
          ) ||
          t('teleattendance.toast_try_again') ||
          'Tente novamente em alguns instantes.',
      });
    },
  });

  const answerTriageMutation = useMutation({
    mutationFn: () =>
      answerPublicTeleattendance(String(token || ''), triageAnswer.trim()),
    onSuccess: async (result) => {
      setTriageAnswer('');
      if (result.queueEntry?.queueStatus === 'waiting') {
        toast.success({
          title:
            t('teleattendance.toast_triage_completed_title') ||
            'Triagem concluída',
          description:
            t('teleattendance.toast_triage_completed_description') ||
            'Sua triagem foi concluída e você entrou na fila.',
        });
      }
      await refetch();
    },
    onError: (error: any) => {
      toast.error({
        title:
          t('teleattendance.toast_triage_answer_error_title') ||
          'Não foi possível enviar sua resposta',
        description:
          translatePublicTeleattendanceError(
            error?.response?.data?.error,
            t as any,
          ) ||
          t('teleattendance.toast_try_again') ||
          'Tente novamente em alguns instantes.',
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelPublicTeleattendanceQueueEntry(String(token || '')),
    onSuccess: async () => {
      toast.success({
        title:
          t('teleattendance.toast_public_leave_success_title') ||
          'Saída da fila confirmada',
        description:
          t('teleattendance.toast_public_leave_success_description') ||
          'Seu status foi atualizado nesta sala.',
      });
      await refetch();
    },
    onError: (error: any) => {
      toast.error({
        title:
          t('teleattendance.toast_public_leave_error_title') ||
          'Não foi possível sair da fila',
        description:
          translatePublicTeleattendanceError(
            error?.response?.data?.error,
            t as any,
          ) ||
          t('teleattendance.toast_try_again') ||
          'Tente novamente em alguns instantes.',
      });
    },
  });

  const canJoin = useMemo(() => {
    if (!session) return false;
    const queueEntry = session.queueEntry;
    if (queueEntry?.queueStatus !== 'pending') return false;

    const now = dayjs();
    if (queueEntry.linkStartsAt && now.isBefore(dayjs(queueEntry.linkStartsAt))) {
      return false;
    }
    if (
      queueEntry.linkExpiresAt &&
      now.isAfter(dayjs(queueEntry.linkExpiresAt))
    ) {
      return false;
    }
    return true;
  }, [session]);

  const isJoinFlowAvailable =
    canJoin && session?.queueEntry?.queueStatus === 'pending';

  const requiresTriage = Boolean(session?.roomEnableTriage);
  const intakeSteps = useMemo<IntakeStep[]>(
    () =>
      requiresTriage
        ? ['details', 'name', 'email', 'triage']
        : ['details', 'name', 'email', 'terms'],
    [requiresTriage],
  );

  const currentStepIndex = intakeSteps.indexOf(currentStep);
  const triageData = session?.queueEntry?.triage;
  const isTriageInProgress =
    triageData?.status === 'in_progress' &&
    Boolean(triageData?.currentQuestion?.trim());
  const isTriageStarted =
    Boolean(triageData?.startedAt) || isTriageInProgress;
  const isSubmittingTriage =
    startTriageMutation.isPending || answerTriageMutation.isPending;
  const isSubmittingFinalTriageAnswer =
    answerTriageMutation.isPending &&
    Boolean(triageSubmissionSnapshot?.isFinalQuestion);
  const triageProcessingTitle = isSubmittingFinalTriageAnswer
    ? t('teleattendance.triage_queueing_title') ||
      'Preparando sua entrada na fila'
    : t('teleattendance.triage_processing_title') ||
      'Organizando sua resposta';
  const triageProcessingDescription = isSubmittingFinalTriageAnswer
    ? t('teleattendance.triage_queueing_description') ||
      'Estamos finalizando sua triagem para encaminhar você à fila de atendimento.'
    : t('teleattendance.triage_processing_description') ||
      'Estamos analisando o que você contou para seguir com a próxima etapa da triagem.';

  const canAdvanceFromStep = useMemo(() => {
    switch (currentStep) {
      case 'details':
        return true;
      case 'name':
        return (
          patientName.trim().length >= 3 &&
          Number(patientAge) >= 0 &&
          Number(patientAge) <= 120 &&
          patientGender.trim().length > 0
        );
      case 'email':
        return /\S+@\S+\.\S+/.test(patientEmail.trim());
      case 'triage':
        return (
          (isTriageStarted && triageAnswer.trim().length >= 2) ||
          (!isTriageStarted && acceptedTerms)
        );
      case 'terms':
        return acceptedTerms;
      default:
        return false;
    }
  }, [
    acceptedTerms,
    currentStep,
    isTriageStarted,
    patientAge,
    patientEmail,
    patientGender,
    patientName,
    triageAnswer,
  ]);

  useEffect(() => {
    if (!isSubmittingTriage) {
      setTriageSubmissionSnapshot(null);
    }
  }, [isSubmittingTriage]);

  const { data: videoConnection, error: videoConnectionError } = useQuery({
    queryKey: [
      'teleattendance-public-video-connection',
      token,
      session?.queueEntry?.id,
      session?.queueEntry?.queueStatus,
    ],
    queryFn: () => fetchPublicTeleattendanceVideoConnection(String(token || '')),
    enabled:
      Boolean(token) &&
      Boolean(session?.queueEntry?.id) &&
      session?.queueEntry?.queueStatus === 'in_progress',
  });

  useEffect(() => {
    if (videoConnection) {
      setIsVideoModalOpen(true);
    }
  }, [videoConnection]);

  useEffect(() => {
    if (!session?.queueEntry) return;
    setPatientName((current) => current || session.queueEntry?.patientName || '');
    setPatientEmail(
      (current) => current || session.queueEntry?.patientEmail || '',
    );
    setPatientAge((current) => {
      if (current) return current;
      if (
        session.queueEntry?.patientAge === null ||
        session.queueEntry?.patientAge === undefined
      ) {
        return current;
      }
      return String(session.queueEntry.patientAge);
    });
    setPatientGender(
      (current) => current || session.queueEntry?.patientGender || '',
    );
    if (session.queueEntry?.triage?.termsAcceptedAt) {
      setAcceptedTerms(true);
    }
  }, [
    session?.queueEntry?.patientAge,
    session?.queueEntry?.patientEmail,
    session?.queueEntry?.patientGender,
    session?.queueEntry?.patientName,
  ]);

  useEffect(() => {
    if (session?.queueEntry?.queueStatus && session.queueEntry.queueStatus !== 'pending') {
      setCurrentStep(requiresTriage ? 'triage' : 'terms');
    }
  }, [requiresTriage, session?.queueEntry?.queueStatus]);

  useEffect(() => {
    if (!requiresTriage) return;
    if (session?.queueEntry?.queueStatus !== 'pending') return;
    if (
      session?.queueEntry?.triage?.status === 'in_progress' ||
      session?.queueEntry?.triage?.status === 'completed'
    ) {
      setCurrentStep('triage');
    }
  }, [requiresTriage, session?.queueEntry?.queueStatus, session?.queueEntry?.triage?.status]);

  useEffect(() => {
    if (intakeSteps.includes(currentStep)) return;
    setCurrentStep(intakeSteps[0]);
  }, [currentStep, intakeSteps]);

  const goToNextStep = () => {
    const nextStep = intakeSteps[currentStepIndex + 1];
    if (nextStep) setCurrentStep(nextStep);
  };

  const goToPreviousStep = () => {
    const previousStep = intakeSteps[currentStepIndex - 1];
    if (previousStep) setCurrentStep(previousStep);
  };

  if (isLoading) {
    return <Loading fullHeight />;
  }

  return (
    <div className="min-h-screen min-h-[100dvh] overflow-x-hidden overflow-y-auto bg-[radial-gradient(circle_at_top,_rgba(186,230,253,0.35),_transparent_24%),linear-gradient(180deg,_#eff6ff_0%,_#f8fbff_45%,_#f8fafc_100%)] p-3 sm:p-5">
      {videoConnection ? (
        <TeleattendanceVideoConference
          connection={videoConnection}
          open={isVideoModalOpen}
          onOpenChange={setIsVideoModalOpen}
          layoutVariant="patient"
          title={
            t('teleattendance.video_consultation_title') || 'Consulta em vídeo'
          }
          description={
            t('teleattendance.patient_video_description') ||
            'O médico fica em destaque no centro da chamada, enquanto sua câmera permanece menor no canto inferior direito.'
          }
        />
      ) : null}

      <div className="mx-auto flex min-h-[calc(100dvh-1.5rem)] w-full max-w-5xl items-stretch justify-center">
        <div className="flex w-full flex-col overflow-hidden rounded-[32px] border border-sky-100/80 bg-[linear-gradient(180deg,_rgba(255,255,255,0.96),_rgba(247,250,252,0.98))] shadow-[0_32px_80px_rgba(15,23,42,0.12)]">
          {isError || !session ? (
            <div className="flex flex-1 items-center justify-center p-8">
              <div className="max-w-md rounded-3xl border border-destructive/20 bg-white/90 p-6 text-center text-sm text-destructive shadow-sm">
                {t('teleattendance.public_not_found') ||
                  'Não foi possível localizar essa sala pública de teleatendimento.'}
              </div>
            </div>
          ) : (
            <>
              <div className="border-b border-slate-200/80 bg-white/80 px-5 py-4 backdrop-blur sm:px-8 sm:py-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="space-y-2">
                    <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-700">
                      <Video className="h-3.5 w-3.5" />
                      {t('teleattendance.public_badge') ||
                        'Teleatendimento Geoescala'}
                    </div>
                    <div>
                      <h1 className="text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                        {t('teleattendance.public_page_title') ||
                          'Sala de Teleatendimento'}
                      </h1>
                      <p className="mt-1 text-sm leading-6 text-slate-600">
                        {session.establishmentName} · {session.roomName}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={[
                        'inline-flex rounded-full border px-3 py-1 text-xs font-medium',
                        sessionStatusTone[session.sessionStatus] ||
                          'border-sky-200 bg-sky-50 text-sky-700',
                      ].join(' ')}
                    >
                      {statusLabel[session.sessionStatus] ||
                        t('teleattendance.waiting_status_short') ||
                        'Aguardando'}
                    </span>
                    {videoConnection ? (
                      <Button
                        variant="outline"
                        className="border-slate-300 bg-white"
                        onClick={() => setIsVideoModalOpen(true)}
                      >
                        <Video className="mr-2 h-4 w-4" />
                        {t('teleattendance.open_video_call') ||
                          'Abrir videochamada'}
                      </Button>
                    ) : null}
                  </div>
                </div>

                {isJoinFlowAvailable ? (
                  <div className="mt-4 hidden gap-2 sm:grid sm:grid-cols-4">
                    {intakeSteps.map((step, index) => {
                      const isActive = currentStep === step;
                      const isDone = currentStepIndex > index;
                      return (
                        <div
                          key={step}
                          className={[
                            'rounded-2xl border px-4 py-3 text-sm transition-colors',
                            isActive
                              ? 'border-sky-300 bg-sky-50 text-sky-800'
                              : isDone
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                : 'border-slate-200 bg-slate-50 text-slate-500',
                          ].join(' ')}
                        >
                          <div className="text-[11px] font-semibold uppercase tracking-[0.18em]">
                            {t('teleattendance.step_label', {
                              step: index + 1,
                            }) || `Etapa ${index + 1}`}
                          </div>
                          <div className="mt-1 font-medium">
                            {step === 'details'
                              ? t('teleattendance.step_details_title') ||
                                'Consulta'
                              : step === 'name'
                                ? t('teleattendance.step_name_title') || 'Nome'
                                : step === 'email'
                                  ? t('teleattendance.step_email_title') ||
                                    'Email'
                                  : step === 'triage'
                                    ? t('teleattendance.step_triage_title') ||
                                      'Triagem'
                                    : t('teleattendance.step_terms_title') ||
                                      'Aceite'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>

              <div className="flex flex-1 flex-col justify-between px-5 py-5 sm:px-8 sm:py-8">
                {isJoinFlowAvailable ? (
                  <>
                    <div className="flex flex-1 items-center justify-center">
                      <div className="w-full max-w-3xl">
                        {currentStep === 'details' ? (
                          <div className="space-y-6">
                            <div className="space-y-2 text-center">
                              <h2 className="text-3xl font-semibold tracking-tight text-slate-950">
                                {t('teleattendance.step_details_title') ||
                                  'Consulta'}
                              </h2>
                              <p className="mx-auto max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
                                {t('teleattendance.step_details_guidance_description') ||
                                  'Confira se este atendimento corresponde à sua consulta. No próximo passo vamos confirmar seu nome, email e os termos de aceite.'}
                              </p>
                            </div>

                            <div className="grid gap-4 md:grid-cols-2">
                              <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-5">
                                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                                  <Hospital className="h-4 w-4 text-sky-700" />
                                  {t('teleattendance.public_establishment_label') ||
                                    'Estabelecimento'}
                                </div>
                                <p className="text-lg font-medium text-slate-800">
                                  {session.establishmentName}
                                </p>
                              </div>

                              <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-5">
                                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                                  <Video className="h-4 w-4 text-sky-700" />
                                  {t('teleattendance.public_room_label') ||
                                    'Sala'}
                                </div>
                                <p className="text-lg font-medium text-slate-800">
                                  {session.roomName}
                                </p>
                              </div>

                              <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-5">
                                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                                  <Clock3 className="h-4 w-4 text-sky-700" />
                                  {t('teleattendance.consultation_window') ||
                                    'Janela da consulta'}
                                </div>
                                <p className="text-lg font-medium text-slate-800">
                                  {session.startTime
                                    ? `${dayjs(session.startTime).format('DD/MM/YYYY HH:mm')} - ${dayjs(session.endTime).format('HH:mm')}`
                                    : dayjs(session.date).format('DD/MM/YYYY')}
                                </p>
                              </div>

                              <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-5">
                                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                                  <Users className="h-4 w-4 text-sky-700" />
                                  {t('teleattendance.public_room_status') ||
                                    'Situação da sala'}
                                </div>
                                <span
                                  className={[
                                    'inline-flex rounded-full border px-3 py-1 text-xs font-medium',
                                    sessionStatusTone[session.sessionStatus] ||
                                      'border-sky-200 bg-sky-50 text-sky-700',
                                  ].join(' ')}
                                >
                                  {statusLabel[session.sessionStatus] ||
                                    t('teleattendance.waiting_status_short') ||
                                    'Aguardando'}
                                </span>
                              </div>

                              {session.specialtyName ? (
                                <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-5">
                                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <UserRound className="h-4 w-4 text-sky-700" />
                                    {t('teleattendance.specialty_fallback') ||
                                      'Especialidade'}
                                  </div>
                                  <p className="text-lg font-medium text-slate-800">
                                    {session.specialtyName}
                                  </p>
                                </div>
                              ) : null}

                              {session.departmentName ? (
                                <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-5">
                                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <Hospital className="h-4 w-4 text-sky-700" />
                                    {t('teleattendance.department_fallback') ||
                                      'Departamento'}
                                  </div>
                                  <p className="text-lg font-medium text-slate-800">
                                    {session.departmentName}
                                  </p>
                                </div>
                              ) : null}
                            </div>

                            {session.queueEntry?.linkStartsAt ||
                            session.queueEntry?.linkExpiresAt ? (
                              <div className="rounded-3xl border border-sky-100 bg-sky-50/70 px-5 py-4 text-sm leading-7 text-slate-700">
                                {t('teleattendance.link_valid_from_to', {
                                  start: session.queueEntry?.linkStartsAt
                                    ? t('teleattendance.link_valid_from_piece', {
                                        time: dayjs(
                                          session.queueEntry.linkStartsAt,
                                        ).format('HH:mm'),
                                      })
                                    : '',
                                  end: session.queueEntry?.linkExpiresAt
                                    ? t('teleattendance.link_valid_until_piece', {
                                        time: dayjs(
                                          session.queueEntry.linkExpiresAt,
                                        ).format('HH:mm'),
                                      })
                                    : '',
                                }) ||
                                  `Link válido${
                                    session.queueEntry?.linkStartsAt
                                      ? ` a partir de ${dayjs(session.queueEntry.linkStartsAt).format('HH:mm')}`
                                      : ''
                                  }${
                                    session.queueEntry?.linkExpiresAt
                                      ? ` até ${dayjs(session.queueEntry.linkExpiresAt).format('HH:mm')}`
                                      : ''
                                  }.`}
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        {currentStep === 'name' ? (
                          <div className="mx-auto w-full max-w-xl space-y-6 text-center">
                            <div className="space-y-2">
                              <h2 className="text-3xl font-semibold tracking-tight text-slate-950">
                                {t('teleattendance.step_name_heading') ||
                                  'Como devemos identificar você?'}
                              </h2>
                              <p className="text-sm leading-7 text-slate-600 sm:text-base">
                                {t('teleattendance.step_name_description') ||
                                  'Informe o nome, a idade e o sexo/gênero que vão contextualizar este atendimento.'}
                              </p>
                            </div>
                            <div className="grid gap-4 text-left sm:grid-cols-2">
                              <div className="space-y-2 sm:col-span-2">
                                <Label htmlFor="patient-name">
                                  {t('teleattendance.full_name_label') ||
                                    'Nome completo'}
                                </Label>
                                <Input
                                  id="patient-name"
                                  value={patientName}
                                  onChange={(event) =>
                                    setPatientName(event.target.value)
                                  }
                                  placeholder={
                                    t('teleattendance.full_name_placeholder') ||
                                    'Digite seu nome'
                                  }
                                  disabled={joinMutation.isPending}
                                  className="h-14 rounded-2xl border-slate-300 bg-white px-4 text-base"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="patient-age">
                                  {t('teleattendance.patient_age_label') ||
                                    'Idade'}
                                </Label>
                                <Input
                                  id="patient-age"
                                  type="number"
                                  min={0}
                                  max={120}
                                  inputMode="numeric"
                                  value={patientAge}
                                  onChange={(event) =>
                                    setPatientAge(event.target.value)
                                  }
                                  placeholder={
                                    t('teleattendance.patient_age_placeholder') ||
                                    'Ex.: 34'
                                  }
                                  disabled={joinMutation.isPending}
                                  className="h-14 rounded-2xl border-slate-300 bg-white px-4 text-base"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="patient-gender">
                                  {t('teleattendance.patient_gender_label') ||
                                    'Sexo/Gênero'}
                                </Label>
                                <Select
                                  value={patientGender}
                                  onValueChange={setPatientGender}
                                  disabled={joinMutation.isPending}
                                >
                                  <SelectTrigger
                                    id="patient-gender"
                                    className="h-14 rounded-2xl border-slate-300 bg-white px-4 text-base"
                                  >
                                    <SelectValue
                                      placeholder={
                                        t('teleattendance.patient_gender_placeholder') ||
                                        'Selecione uma opção'
                                      }
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="female">
                                      {t('teleattendance.patient_gender_female') ||
                                        'Feminino'}
                                    </SelectItem>
                                    <SelectItem value="male">
                                      {t('teleattendance.patient_gender_male') ||
                                        'Masculino'}
                                    </SelectItem>
                                    <SelectItem value="non_binary">
                                      {t('teleattendance.patient_gender_non_binary') ||
                                        'Não binário'}
                                    </SelectItem>
                                    <SelectItem value="other">
                                      {t('teleattendance.patient_gender_other') ||
                                        'Outro'}
                                    </SelectItem>
                                    <SelectItem value="prefer_not_to_say">
                                      {t('teleattendance.patient_gender_prefer_not_to_say') ||
                                        'Prefiro não informar'}
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                        ) : null}

                        {currentStep === 'email' ? (
                          <div className="mx-auto w-full max-w-xl space-y-6 text-center">
                            <div className="space-y-2">
                              <h2 className="text-3xl font-semibold tracking-tight text-slate-950">
                                {t('teleattendance.step_email_heading') ||
                                  'Qual email podemos usar se precisarmos falar com você?'}
                              </h2>
                              <p className="text-sm leading-7 text-slate-600 sm:text-base">
                                {t('teleattendance.step_email_description') ||
                                  'Esse email poderá ser usado para contato posterior relacionado a este atendimento.'}
                              </p>
                            </div>
                            <div className="space-y-2 text-left">
                              <Label htmlFor="patient-email">
                                {t('teleattendance.contact_email_label') ||
                                  'Email para contato'}
                              </Label>
                              <Input
                                id="patient-email"
                                type="email"
                                value={patientEmail}
                                onChange={(event) =>
                                  setPatientEmail(event.target.value)
                                }
                                placeholder={
                                  t('teleattendance.contact_email_placeholder') ||
                                  'voce@exemplo.com'
                                }
                                disabled={joinMutation.isPending}
                                className="h-14 rounded-2xl border-slate-300 bg-white px-4 text-base"
                              />
                            </div>
                          </div>
                        ) : null}

                        {currentStep === 'triage' ? (
                          <div className="mx-auto w-full max-w-3xl space-y-6">
                            <div className="space-y-2 text-center">
                              <h2 className="text-3xl font-semibold tracking-tight text-slate-950">
                                {t('teleattendance.step_triage_heading') ||
                                  'Triagem inicial antes da fila'}
                              </h2>
                              <p className="text-sm leading-7 text-slate-600 sm:text-base">
                                {t('teleattendance.step_triage_description') ||
                                  'Vamos registrar algumas informações rápidas para dar mais contexto ao médico antes da sua consulta.'}
                              </p>
                            </div>

                            {!isTriageStarted ? (
                              <div className="space-y-4">
                                <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-6 text-sm leading-7 text-slate-700 sm:text-base">
                                  {t('teleattendance.step_triage_terms_placeholder') ||
                                    'Ao iniciar a triagem, suas respostas serão usadas para contextualizar o profissional de saúde antes do atendimento. Você pode responder de forma objetiva e seguir nesta página até a consulta começar.'}
                                </div>
                                <div className="flex items-start gap-3 rounded-3xl border border-slate-200 bg-white px-5 py-4">
                                  <Checkbox
                                    id="teleattendance-triage-terms"
                                    checked={acceptedTerms}
                                    onCheckedChange={(checked) =>
                                      setAcceptedTerms(checked === true)
                                    }
                                  />
                                  <Label
                                    htmlFor="teleattendance-triage-terms"
                                    className="cursor-pointer text-sm leading-7 text-slate-700 sm:text-base"
                                  >
                                    {t(
                                      'teleattendance.step_triage_terms_accept_label',
                                    ) ||
                                      'Li e aceito seguir com a triagem deste teleatendimento.'}
                                  </Label>
                                </div>
                              </div>
                            ) : null}

                            {isTriageStarted ? (
                              <div className="space-y-4">
                                {triageData?.transcript?.length ? (
                                  <div className="rounded-3xl border border-slate-200 bg-slate-50/70 p-5">
                                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                      {t('teleattendance.triage_transcript_title') ||
                                        'Resumo do que você já respondeu'}
                                    </p>
                                    <div className="mt-3 space-y-3">
                                      {triageData.transcript.map((turn, index) => (
                                        <div
                                          key={`${turn.question}-${index}`}
                                          className="rounded-2xl border border-slate-200 bg-white px-4 py-3"
                                        >
                                          <p className="text-sm font-medium text-slate-900">
                                            {turn.question}
                                          </p>
                                          <p className="mt-2 text-sm leading-6 text-slate-600">
                                            {turn.answer}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ) : null}

                                {triageData?.currentQuestion ? (
                                  <div className="rounded-3xl border border-sky-200 bg-sky-50/70 p-6">
                                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
                                      {t('teleattendance.triage_current_question_label') ||
                                        'Pergunta atual'}
                                    </p>
                                    <p className="mt-3 text-xl font-medium leading-8 text-slate-950">
                                      {triageData.currentQuestion}
                                    </p>
                                  </div>
                                ) : null}

                                <div className="space-y-2">
                                  <Label htmlFor="teleattendance-triage-answer">
                                    {t('teleattendance.triage_answer_label') ||
                                      'Sua resposta'}
                                  </Label>
                                  <Textarea
                                    id="teleattendance-triage-answer"
                                    value={triageAnswer}
                                    onChange={(event) =>
                                      setTriageAnswer(event.target.value)
                                    }
                                    placeholder={
                                      t(
                                        'teleattendance.triage_answer_placeholder',
                                      ) ||
                                      'Escreva sua resposta com o máximo de clareza possível.'
                                    }
                                    disabled={answerTriageMutation.isPending}
                                    className="min-h-[160px] rounded-3xl border-slate-300 bg-white px-4 py-4 text-base"
                                  />
                                </div>

                                {isSubmittingTriage ? (
                                  <div className="rounded-2xl border border-sky-200 bg-sky-50/80 px-4 py-3 text-sm text-sky-800">
                                    <div className="flex items-center gap-3">
                                      <div className="flex items-center gap-1.5">
                                        <span className="h-2 w-2 animate-bounce rounded-full bg-sky-500 [animation-delay:-0.24s]" />
                                        <span className="h-2 w-2 animate-bounce rounded-full bg-sky-500 [animation-delay:-0.12s]" />
                                        <span className="h-2 w-2 animate-bounce rounded-full bg-sky-500" />
                                      </div>
                                      <div className="min-w-0">
                                        <p className="font-medium text-sky-900">
                                          {triageProcessingTitle}
                                        </p>
                                        <p className="mt-1 text-sky-700">
                                          {triageProcessingDescription}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        {currentStep === 'terms' ? (
                          <div className="mx-auto w-full max-w-3xl space-y-6">
                            <div className="space-y-2 text-center">
                              <h2 className="text-3xl font-semibold tracking-tight text-slate-950">
                                {t('teleattendance.step_terms_heading') ||
                                  'Aceite antes de entrar na fila'}
                              </h2>
                              <p className="text-sm leading-7 text-slate-600 sm:text-base">
                                {t('teleattendance.step_terms_description') ||
                                  'Em breve vamos substituir este texto pelos termos oficiais do teleatendimento. Por enquanto, use este bloco como placeholder de aceite.'}
                              </p>
                            </div>
                            <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-6 text-sm leading-7 text-slate-700 sm:text-base">
                              {t('teleattendance.step_terms_placeholder') ||
                                'Declaro que estou ciente de que este atendimento ocorrerá por teleatendimento, que meus dados serão usados para identificar minha entrada na fila e que devo aguardar nesta página até o profissional iniciar a consulta.'}
                            </div>
                            <div className="flex items-start gap-3 rounded-3xl border border-slate-200 bg-white px-5 py-4">
                              <Checkbox
                                id="teleattendance-terms"
                                checked={acceptedTerms}
                                onCheckedChange={(checked) =>
                                  setAcceptedTerms(checked === true)
                                }
                              />
                              <Label
                                htmlFor="teleattendance-terms"
                                className="cursor-pointer text-sm leading-7 text-slate-700 sm:text-base"
                              >
                                {t('teleattendance.step_terms_accept_label') ||
                                  'Li e aceito os termos deste atendimento remoto.'}
                              </Label>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {!canJoin ? (
                      <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        {t('teleattendance.link_not_available_now') ||
                          'Este link ainda não está disponível para entrada ou já expirou. Aguarde a janela correta ou solicite um novo link.'}
                      </div>
                    ) : null}

                    <div className="flex flex-col gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                      <Button
                        type="button"
                        variant="outline"
                        className="h-12 rounded-2xl border-slate-300 bg-white px-6"
                        disabled={
                          currentStepIndex === 0 ||
                          joinMutation.isPending ||
                          startTriageMutation.isPending ||
                          answerTriageMutation.isPending
                        }
                        onClick={goToPreviousStep}
                      >
                        {t('common.back') || 'Voltar'}
                      </Button>

                      {currentStep === 'triage' ? (
                        <Button
                          type="button"
                          className="h-12 rounded-2xl px-8"
                          disabled={
                            !canAdvanceFromStep ||
                            !patientName.trim() ||
                            !patientEmail.trim() ||
                            !patientAge ||
                            !patientGender.trim() ||
                            !canJoin ||
                            startTriageMutation.isPending ||
                            answerTriageMutation.isPending
                          }
                          onClick={() => {
                            if (!isTriageStarted) {
                              startTriageMutation.mutate();
                              return;
                            }
                            setTriageSubmissionSnapshot({
                              answer: triageAnswer.trim(),
                              isFinalQuestion:
                                (triageData?.transcript?.length || 0) + 1 >=
                                TRIAGE_MAX_QUESTIONS,
                            });
                            answerTriageMutation.mutate();
                          }}
                        >
                          {isSubmittingTriage ? (
                            <span className="mr-2 flex items-center gap-1.5">
                              <span className="h-2 w-2 animate-bounce rounded-full bg-current [animation-delay:-0.24s]" />
                              <span className="h-2 w-2 animate-bounce rounded-full bg-current [animation-delay:-0.12s]" />
                              <span className="h-2 w-2 animate-bounce rounded-full bg-current" />
                            </span>
                          ) : (
                            <UserRound className="mr-2 h-4 w-4" />
                          )}
                          {!isTriageStarted
                            ? t('teleattendance.start_triage_cta') ||
                              'Iniciar triagem'
                            : isSubmittingTriage
                              ? triageProcessingTitle
                              : t('teleattendance.next_triage_question_cta') ||
                                'Enviar resposta'}
                        </Button>
                      ) : currentStep !== 'terms' ? (
                        <Button
                          type="button"
                          className="h-12 rounded-2xl px-8"
                          disabled={
                            !canAdvanceFromStep ||
                            joinMutation.isPending ||
                            startTriageMutation.isPending ||
                            answerTriageMutation.isPending
                          }
                          onClick={goToNextStep}
                        >
                          {t('teleattendance.next_step_cta') || 'Continuar'}
                        </Button>
                      ) : (
                        <Button
                          className="h-12 rounded-2xl px-8 shadow-sm"
                          disabled={
                            !patientName.trim() ||
                            !patientEmail.trim() ||
                            !patientAge ||
                            !patientGender.trim() ||
                            !acceptedTerms ||
                            !canJoin ||
                            joinMutation.isPending ||
                            startTriageMutation.isPending ||
                            answerTriageMutation.isPending
                          }
                          onClick={() => joinMutation.mutate()}
                        >
                          <UserRound className="mr-2 h-4 w-4" />
                          {t('teleattendance.enter_queue_cta') ||
                            'Entrar na fila'}
                        </Button>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-1 items-center justify-center">
                    <div className="w-full max-w-3xl space-y-5">
                      <div className="space-y-2 text-center">
                        <h2 className="text-3xl font-semibold tracking-tight text-slate-950">
                          {queueStatusLabel[session.queueEntry?.queueStatus || 'waiting'] ||
                            t('teleattendance.waiting_status_short') ||
                            'Aguardando'}
                        </h2>
                        <p className="text-sm leading-7 text-slate-600 sm:text-base">
                          {t('teleattendance.patient_journey_locked') ||
                            'Sua entrada guiada já foi concluída ou este link não está mais disponível. Acompanhe abaixo o status do seu atendimento.'}
                        </p>
                      </div>

                      {session.queueEntry ? (
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="rounded-3xl border border-slate-200 bg-white px-5 py-5 shadow-sm">
                            <div className="mb-3 flex items-center justify-between gap-2">
                              <p className="font-semibold text-slate-900">
                                {t('teleattendance.identified_patient') ||
                                  'Paciente identificado'}
                              </p>
                              <span
                                className={[
                                  'inline-flex rounded-full border px-3 py-1 text-xs font-medium',
                                  queueStatusTone[
                                    session.queueEntry.queueStatus
                                  ] ||
                                    'border-slate-200 bg-slate-100 text-slate-700',
                                ].join(' ')}
                              >
                                {queueStatusLabel[
                                  session.queueEntry.queueStatus
                                ] ||
                                  t('common.status') ||
                                  'Status atualizado'}
                              </span>
                            </div>
                            <p className="text-lg font-medium text-slate-900">
                              {session.queueEntry.patientName}
                            </p>
                            {session.queueEntry.patientEmail ? (
                              <p className="mt-1 text-sm text-slate-600">
                                {session.queueEntry.patientEmail}
                              </p>
                            ) : null}
                            {session.queueEntry.patientAge !== null ||
                            session.queueEntry.patientGender ? (
                              <p className="mt-1 text-sm text-slate-600">
                                {[
                                  session.queueEntry.patientAge !== null &&
                                  session.queueEntry.patientAge !== undefined
                                    ? t('teleattendance.patient_age_value', {
                                        age: session.queueEntry.patientAge,
                                      }) || `${session.queueEntry.patientAge} anos`
                                    : '',
                                  session.queueEntry.patientGender
                                    ? t(
                                        `teleattendance.patient_gender_${session.queueEntry.patientGender}`,
                                      ) || session.queueEntry.patientGender
                                    : '',
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </p>
                            ) : null}
                            {session.queueEntry.claimedAt ? (
                              <p className="mt-4 text-sm text-slate-600">
                                {t('teleattendance.confirmed_at', {
                                  time: dayjs(session.queueEntry.claimedAt).format(
                                    'HH:mm',
                                  ),
                                }) ||
                                  `Identificação confirmada às ${dayjs(session.queueEntry.claimedAt).format('HH:mm')}.`}
                              </p>
                            ) : null}
                            {session.queueEntry.position ? (
                              <p className="mt-2 text-sm text-slate-600">
                                {t('teleattendance.queue_position', {
                                  position: session.queueEntry.position,
                                }) ||
                                  `Posição atual na fila: ${session.queueEntry.position}`}
                              </p>
                            ) : null}
                          </div>

                          <div className="rounded-3xl border border-slate-200 bg-white px-5 py-5 shadow-sm">
                            <div className="mb-3 flex items-center gap-2 font-semibold text-slate-900">
                              <Clock3 className="h-4 w-4 text-sky-700" />
                              {t('teleattendance.consultation_progress_title') ||
                                'Andamento da consulta'}
                            </div>
                            <div className="space-y-2 text-sm leading-7 text-slate-600">
                              <p>
                                {session.queueEntry.startedAt
                                  ? t(
                                      'teleattendance.consultation_started_at',
                                      {
                                        time: dayjs(
                                          session.queueEntry.startedAt,
                                        ).format('HH:mm'),
                                      },
                                    ) ||
                                    `Sua consulta foi iniciada às ${dayjs(session.queueEntry.startedAt).format('HH:mm')}.`
                                  : t(
                                      'teleattendance.consultation_not_started',
                                    ) ||
                                    'Sua consulta ainda não foi iniciada.'}
                              </p>
                              {session.queueEntry.startedAt ? (
                                <p>
                                  {t(
                                    'teleattendance.time_in_consultation',
                                    {
                                      value: formatElapsedTime(
                                        session.queueEntry.startedAt,
                                      ),
                                    },
                                  ) ||
                                    `Tempo em atendimento: ${formatElapsedTime(session.queueEntry.startedAt)}`}
                                </p>
                              ) : null}
                              <p>
                                {session.queueEntry.endedAt
                                  ? t(
                                      'teleattendance.consultation_ended_at',
                                      {
                                        time: dayjs(
                                          session.queueEntry.endedAt,
                                        ).format('HH:mm'),
                                      },
                                    ) ||
                                    `Sua consulta foi encerrada às ${dayjs(session.queueEntry.endedAt).format('HH:mm')}.`
                                  : t(
                                      'teleattendance.consultation_not_ended',
                                    ) ||
                                    'Sua consulta ainda não foi encerrada.'}
                              </p>
                              <p className="rounded-2xl border border-sky-100 bg-sky-50/80 px-4 py-3 text-slate-700">
                                {videoConnectionError
                                  ? t(
                                      'teleattendance.video_unavailable_environment',
                                    ) ||
                                    'A videoconferência ainda não está disponível neste ambiente.'
                                  : t(
                                      'teleattendance.public_video_auto_open_hint',
                                    ) ||
                                    'Quando o profissional iniciar o seu atendimento, a chamada abrirá aqui automaticamente.'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50/80 px-5 py-6 text-center text-sm text-slate-600">
                          {t('teleattendance.not_in_queue') ||
                            'Você ainda não entrou na fila desta sala.'}
                        </div>
                      )}

                      {session.queueEntry?.queueStatus === 'waiting' ? (
                        <div className="flex justify-center">
                          <Button
                            variant="outline"
                            className="h-12 rounded-2xl border-slate-300 bg-white px-8"
                            disabled={cancelMutation.isPending}
                            onClick={() => cancelMutation.mutate()}
                          >
                            {t('teleattendance.leave_queue') ||
                              'Sair da fila'}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeleattendancePublicPage;
